/**
 * Document Processing Utilities
 * Centralized logic for handling document processing status and URL resolution
 */

/**
 * Ensure a URL has https:// prefix
 * @param {string} url - URL to process
 * @returns {string} URL with https:// prefix
 */
function ensureHttps(url) {
  if (!url) return url;
  return url.startsWith('http') ? url : `https://${url}`;
}

/**
 * Get thumbnail URL for a document, considering processing status
 * @param {Object} content - Document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {string|null} Thumbnail URL or null
 */
export function getDocumentThumbnailUrl(content, documentProcessingStatuses = {}) {
  if (!content) return null;

  // Get processing status data
  const processingStatusData = documentProcessingStatuses[content.id];

  // If we have processing status data and should use temporary URLs
  if (processingStatusData) {
    const shouldUseTemporaryUrls = processingStatusData.state === 'process_document' || (processingStatusData.optimized_url && processingStatusData.placeholder_url);

    if (shouldUseTemporaryUrls && processingStatusData.placeholder_url) {
      return ensureHttps(processingStatusData.placeholder_url);
    }
  }

  // Use permanent thumbnail URLs (optimized_url is now the real thumbnail)
  const thumbnailUrl = content.optimized_url || content.preview_url || content.placeholder_url;

  return thumbnailUrl ? ensureHttps(thumbnailUrl) : null;
}

/**
 * Get document file URL for actual document viewing/downloading
 * @param {Object} content - Document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {string|null} Document file URL or null
 */
export function getDocumentFileUrl(content, documentProcessingStatuses = {}) {
  if (!content) return null;

  // Check if we have processing status data and should use temporary URLs
  const processingStatusData = documentProcessingStatuses[content.id];
  if (processingStatusData) {
    const shouldUseTemporaryUrls = processingStatusData.state === 'process_document' || (processingStatusData.optimized_url && processingStatusData.placeholder_url);

    if (shouldUseTemporaryUrls && processingStatusData.optimized_url) {
      return processingStatusData.optimized_url;
    }
  }

  // For downloads, always use the original document URL (content.url)
  // The extra_attributes.pdf_file_url is only for viewing converted PDFs, not downloading original files
  return content.url || null;
}

/**
 * Get document viewing URL (prioritizes PDF version for DOC/DOCX for better viewing experience)
 * @param {Object} content - Document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {string|null} Document viewing URL or null
 */
export function getDocumentViewingUrl(content, documentProcessingStatuses = {}) {
  if (!content) return null;

  // Check if we have processing status data and should use temporary URLs
  const processingStatusData = documentProcessingStatuses[content.id];
  if (processingStatusData) {
    const shouldUseTemporaryUrls = processingStatusData.state === 'process_document' || (processingStatusData.optimized_url && processingStatusData.placeholder_url);

    if (shouldUseTemporaryUrls && processingStatusData.optimized_url) {
      return processingStatusData.optimized_url;
    }
  }

  // For viewing, prioritize PDF version for better browser compatibility (especially for DOC/DOCX)
  // but fallback to original document URL if no PDF version is available
  const viewingUrl = content.extra_attributes?.pdf_file_url || content.url;

  return viewingUrl || null;
}

/**
 * Get enhanced document content object with processing status URLs
 * @param {Object} content - Original document content object
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {Object} Enhanced content object with updated URLs
 */
export function getEnhancedDocumentContent(content, documentProcessingStatuses = {}) {
  if (!content) return content;

  // Only enhance document content
  if (content.type !== 'document' && content.content_type !== 'document') {
    return content;
  }

  // Check if we have processing status data
  const processingStatusData = documentProcessingStatuses[content.id];

  // If no processing status data, use the new permanent URLs structure
  if (!processingStatusData) {
    return content; // Content now has proper optimized_url (thumbnail) and extra_attributes.pdf_file_url (document)
  }

  // Check if we should use temporary URLs from processing status
  const shouldUseTemporaryUrls = processingStatusData.state === 'process_document' || (processingStatusData.optimized_url && processingStatusData.placeholder_url);

  // If we should use temporary URLs, replace with processing status URLs
  if (shouldUseTemporaryUrls) {
    const enhancedContent = { ...content };

    // Replace thumbnail URLs with temporary ones if available
    if (processingStatusData.placeholder_url) {
      enhancedContent.preview_url = processingStatusData.placeholder_url;
      enhancedContent.placeholder_url = processingStatusData.placeholder_url;
      enhancedContent.optimized_url = processingStatusData.placeholder_url; // Override with temp thumbnail
    }

    // Replace document URL with temporary one if available
    if (processingStatusData.optimized_url) {
      enhancedContent.url = processingStatusData.optimized_url;
      // Keep extra_attributes.pdf_file_url as is for permanent document access
    }

    return enhancedContent;
  }

  // Otherwise, use the permanent URLs (optimized_url = thumbnail, extra_attributes.pdf_file_url = document)
  return content;
}

/**
 * Batch enhance multiple document content objects
 * @param {Array} contentArray - Array of content objects
 * @param {Object} documentProcessingStatuses - All document processing statuses
 * @returns {Array} Array of enhanced content objects
 */
export function enhanceDocumentContentBatch(contentArray, documentProcessingStatuses = {}) {
  if (!Array.isArray(contentArray)) return contentArray;

  return contentArray.map(content => getEnhancedDocumentContent(content, documentProcessingStatuses));
}
